import React from 'react';
import {
  CartesianGrid,
  Dot,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BucketPoint } from '../../../lib/metricsUtils';

interface DetailMetricsChartProps {
  data: BucketPoint[];
  granularity?: 'daily' | 'hourly';
}

const formatCompact = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }
  return `${Math.round(value)}`;
};

const DetailMetricsChart: React.FC<DetailMetricsChartProps> = ({ data, granularity = 'daily' }) => {
  const safe = data.length > 0
    ? data
    : [0, 1, 2, 3, 4, 5].map((index) => ({
      bucket: new Date(Date.now() - ((5 - index) * 24 * 60 * 60 * 1000)).toISOString(),
      value: 0,
    }));

  const tickFormatter = (bucket: string): string => {
    const date = new Date(bucket);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    if (granularity === 'hourly') {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        hour12: true,
      });
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const maxValue = Math.max(...safe.map((point) => point.value), 0);
  const yMax = maxValue > 0 ? Math.ceil(maxValue * 1.25) : 10;

  return (
    <div style={{ width: '100%', height: 235 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={safe} margin={{ top: 12, right: 6, bottom: 14, left: 10 }}>
          <CartesianGrid stroke="#e7edf6" vertical={false} strokeDasharray="2 3" />
          <XAxis
            dataKey="bucket"
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#6c7b97', fontSize: 12 }}
            dy={8}
            minTickGap={granularity === 'hourly' ? 32 : 20}
            tickFormatter={tickFormatter}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#6c7b97', fontSize: 12 }}
            domain={[0, yMax]}
            ticks={[0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax].map((value) => Math.round(value))}
            tickFormatter={formatCompact}
            width={48}
          />
          <Tooltip
            formatter={(value: number) => [formatCompact(value), 'Events']}
            labelFormatter={(bucket: string) => {
              const date = new Date(bucket);
              if (Number.isNaN(date.getTime())) {
                return bucket;
              }

              if (granularity === 'daily') {
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                });
              }

              return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              });
            }}
            contentStyle={{
              border: '1px solid #d5deec',
              borderRadius: 8,
              boxShadow: '0 4px 14px rgba(13, 36, 69, 0.12)',
              fontSize: 12,
            }}
            labelStyle={{ color: '#506482' }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#586987"
            strokeWidth={3}
            dot={<Dot r={3} fill="#5f708f" stroke="#ffffff" strokeWidth={2} />}
            activeDot={{ r: 5, fill: '#2f5be7' }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DetailMetricsChart;
